import type { PartFileUnits } from '@frontend/api';
import type {
  PreparedFeature,
  PreparedFeedbackItem,
  PreparedViewerItem,
} from '@frontend/partviewer';
import type {
  STOCK_TYPES,
  WEIGHT_UNITS,
} from '@frontend/partviewer/src/constants';
import type { Comparison } from '@frontend/partviewer/src/renderer/caliper-utils';
import type { AnyIntersectionTest } from '@frontend/partviewer/src/renderer/intersection-utils';
import type {
  LiteObject,
  LitState,
} from '@frontend/partviewer/src/renderer/load-utils';
import type {
  Highlight,
  MetaData,
  Selection,
} from '@frontend/partviewer/src/renderer/renderer-component';
import type {
  RawResultForCreation,
  ResultForCreation,
} from '@frontend/partviewer/src/renderer/stock-utils';

import type { ViewerPartFile } from 'modules/common/viewer/types';

import type { ActionsOf } from 'store/types';

/**
 * Slim interrogation context for CAD_VIEWER_RECEIVE_INTERROGATION_INFO.
 * All fields optional so callers can pass `{}` to clear (JS-era convention:
 * destructuring yields undefineds, which the reducer writes through).
 */
export type CadViewerInterrogationInfoPayload = {
  documentHash?: string;
  interrogationFileId?: number | null;
  task?: string;
};

/**
 * Payload for CAD_VIEWER_SET_GEOMETRIC_ENTITIES.
 * `{}` / omitted keys → saga and reducer coerce to [].
 */
export type SetGeometricEntitiesPayload = {
  showAdditionalKeysExplicit?: string[];
};

export const types = {
  CAD_VIEWER_APPEND_VISIBLE_BODIES:
    'common/viewer/cadViewer/CAD_VIEWER_APPEND_VISIBLE_BODIES',
  CAD_VIEWER_RECEIVE_ACTIVE_BODIES:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_ACTIVE_BODIES',
  CAD_VIEWER_RECEIVE_ASSEMBLY_TREE_DATA:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_ASSEMBLY_TREE_DATA',
  CAD_VIEWER_RECEIVE_DISPLAY_SCALE:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_DISPLAY_SCALE',
  CAD_VIEWER_RECEIVE_DISPLAY_UNITS:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_DISPLAY_UNITS',
  CAD_VIEWER_RECEIVE_DIMENSIONAL_PRECISION:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_DIMENSIONAL_PRECISION',
  CAD_VIEWER_RECEIVE_DISPLAY_WEIGHT_UNITS:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_DISPLAY_WEIGHT_UNITS',
  CAD_VIEWER_RECEIVE_ENABLED_BODIES:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_ENABLED_BODIES',
  CAD_VIEWER_RECEIVE_EXPANDED_BODIES:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_EXPANDED_BODIES',
  CAD_VIEWER_RECEIVE_FOCUSED_BODIES:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_FOCUSED_BODIES',
  CAD_VIEWER_RECEIVE_FOCUSED_UUID:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_FOCUSED_UUID',
  CAD_VIEWER_RECEIVE_FORCE_FOCUS:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_FORCE_FOCUS',
  CAD_VIEWER_RECEIVE_FORCE_VIEW:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_FORCE_VIEW',
  CAD_VIEWER_RECEIVE_GEOMETRIC_DOCUMENT_HASH:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_GEOMETRIC_DOCUMENT_HASH',
  CAD_VIEWER_RECEIVE_HAS_PMI_ANNOTATIONS:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_HAS_PMI_ANNOTATIONS',
  CAD_VIEWER_RECEIVE_HIGHLIGHT:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_HIGHLIGHT',
  CAD_VIEWER_RECEIVE_HOVER_BODY:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_HOVER_BODY',
  CAD_VIEWER_RECEIVE_INTERROGATION_INFO:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_INTERROGATION_INFO',
  CAD_VIEWER_RECEIVE_META_DATA:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_META_DATA',
  CAD_VIEWER_RECEIVE_NATIVE_UNITS:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_NATIVE_UNITS',
  CAD_VIEWER_RECEIVE_PART_FILE:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_PART_FILE',
  CAD_VIEWER_RECEIVE_PROCESS_TAG:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_PROCESS_TAG',
  CAD_VIEWER_RECEIVE_SELECTED_BODIES:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_SELECTED_BODIES',
  CAD_VIEWER_RECEIVE_SHOW_DXF_CONTROLS:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_SHOW_DXF_CONTROLS',
  CAD_VIEWER_RECEIVE_SHOW_ADDITIONAL_KEYS:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_SHOW_ADDITIONAL_KEYS',
  CAD_VIEWER_RECEIVE_SHOW_ADDITIONAL_KEYS_EXPLICIT:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_SHOW_ADDITIONAL_KEYS_EXPLICIT',
  CAD_VIEWER_RECEIVE_TEMPORARILY_SIMPLIFIED:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_TEMPORARILY_SIMPLIFIED',
  CAD_VIEWER_RECEIVE_TOO_COMPLEX:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_TOO_COMPLEX',
  CAD_VIEWER_RECEIVE_VISIBLE_BODIES:
    'common/viewer/cadViewer/CAD_VIEWER_RECEIVE_VISIBLE_BODIES',
  CAD_VIEWER_REMOVE_VISIBLE_BODIES:
    'common/viewer/cadViewer/CAD_VIEWER_REMOVE_VISIBLE_BODIES',
  CAD_VIEWER_RESET: 'common/viewer/cadViewer/CAD_VIEWER_RESET',
  CAD_VIEWER_TOGGLE_SELECTED_FEATURES:
    'common/viewer/cadViewer/CAD_VIEWER_TOGGLE_SELECTED_FEATURES',
  CAD_VIEWER_SET_GEOMETRIC_ENTITIES:
    'common/viewer/cadViewer/CAD_VIEWER_SET_GEOMETRIC_ENTITIES',
  CAD_VIEWER_SET_RENDER_NATIVE_COLORS:
    'common/viewer/cadViewer/CAD_VIEWER_SET_RENDER_NATIVE_COLORS',
  DIMENSION_TOOL_TOGGLE: 'common/viewer/cadViewer/DIMENSION_TOOL_TOGGLE',
  CROSS_SECTION_SET_ENABLED:
    'common/viewer/cadViewer/CROSS_SECTION_SET_ENABLED',
  CROSS_SECTION_SET_POSITION:
    'common/viewer/cadViewer/CROSS_SECTION_SET_POSITION',
  CROSS_SECTION_TOGGLE_INVERTED:
    'common/viewer/cadViewer/CROSS_SECTION_TOGGLE_INVERTED',
  CROSS_SECTION_SET_PICK_MODE:
    'common/viewer/cadViewer/CROSS_SECTION_SET_PICK_MODE',
  CROSS_SECTION_SET_NORMAL: 'common/viewer/cadViewer/CROSS_SECTION_SET_NORMAL',
  EXPLODED_VIEW_TOOL_DISABLE:
    'common/viewer/cadViewer/EXPLODED_VIEW_TOOL_DISABLE',
  EXPLODED_VIEW_TOOL_RECEIVE_EXPLODED_STATE:
    'common/viewer/cadViewer/EXPLODED_VIEW_TOOL_RECEIVE_EXPLODED_STATE',
  EXPLODED_VIEW_TOOL_TOGGLE:
    'common/viewer/cadViewer/EXPLODED_VIEW_TOOL_TOGGLE',
  FEATURE_SELECTION_RECEIVE:
    'common/viewer/cadViewer/FEATURE_SELECTION_RECEIVE',
  FEATURE_SNAP_TO_RECEIVE: 'common/viewer/cadViewer/FEATURE_SNAP_TO_RECEIVE',
  FEATURE_SNAP_TO_REQUEST: 'common/viewer/cadViewer/FEATURE_SNAP_TO_REQUEST',
  FOCUSED_VIEW_TOGGLE: 'common/viewer/cadViewer/FOCUSED_VIEW_TOGGLE',
  GEOMETRIC_PART_FILE_BULK_PREPARE:
    'common/viewer/cadViewer/GEOMETRIC_PART_FILE_BULK_PREPARE',
  HIGHLIGHT_VIEW_TOGGLE: 'common/viewer/cadViewer/HIGHLIGHT_VIEW_TOGGLE',
  MANUFACTURABILITY_FEEDBACK_SELECTION_RECEIVE:
    'common/viewer/cadViewer/MANUFACTURABILITY_FEEDBACK_SELECTION_RECEIVE',
  MATERIAL_STOCK_CREATE_FAILED:
    'common/viewer/cadViewer/MATERIAL_STOCK_CREATE_FAILED',
  MATERIAL_STOCK_CREATE_SUCCESS:
    'common/viewer/cadViewer/MATERIAL_STOCK_CREATE_SUCCESS',
  MATERIAL_STOCK_FINISH_NESTING_CREATION:
    'common/viewer/cadViewer/MATERIAL_STOCK_FINISH_NESTING_CREATION',
  MATERIAL_STOCK_RECEIVE_NEW_RESULT:
    'common/viewer/cadViewer/MATERIAL_STOCK_RECEIVE_NEW_RESULT',
  MATERIAL_STOCK_RECEIVE_VISIBLE_KEYS:
    'common/viewer/cadViewer/MATERIAL_STOCK_RECEIVE_VISIBLE_KEYS',
  MEASUREMENT_TOOL_TOGGLE: 'common/viewer/cadViewer/MEASUREMENT_TOOL_TOGGLE',
  MEASUREMENT_TOOL_RECEIVE_COMPARISON:
    'common/viewer/cadViewer/MEASUREMENT_TOOL_RECEIVE_COMPARISON',
  MEASUREMENT_TOOL_RECEIVE_SELECTIONS:
    'common/viewer/cadViewer/MEASUREMENT_TOOL_RECEIVE_SELECTIONS',
  MEASUREMENT_TOOL_REMOVE_SELECTIONS:
    'common/viewer/cadViewer/MEASUREMENT_TOOL_REMOVE_SELECTIONS',
  MEASUREMENT_TOOL_RECEIVE_MEASUREMENT:
    'common/viewer/cadViewer/MEASUREMENT_TOOL_RECEIVE_MEASUREMENT',
  MEASUREMENT_TOOL_ENABLE: 'common/viewer/cadViewer/MEASUREMENT_TOOL_ENABLE',
  MEASUREMENT_TOOL_DISABLE: 'common/viewer/cadViewer/MEASUREMENT_TOOL_DISABLE',
  MEASUREMENT_OBJECTS_MANAGER_RECEIVE_SELECTIONS:
    'common/viewer/cadViewer/MEASUREMENT_OBJECTS_MANAGER_RECEIVE_SELECTIONS',
  MEASUREMENT_OBJECTS_MANAGER_REMOVE_SELECTIONS:
    'common/viewer/cadViewer/MEASUREMENT_OBJECTS_MANAGER_REMOVE_SELECTIONS',
  OPTIMAL_BOUNDING_BOX_TOOL_TOGGLE:
    'common/viewer/cadViewer/OPTIMAL_BOUNDING_BOX_TOOL_TOGGLE',
  PMI_VIEW_TOGGLE: 'common/viewer/cadViewer/PMI_VIEW_TOGGLE',
  PMI_VIEW_SHOW: 'common/viewer/cadViewer/PMI_VIEW_SHOW',
  PMI_VIEW_HIDE: 'common/viewer/cadViewer/PMI_VIEW_HIDE',
  RENDER_NATIVE_COLORS_TOGGLE:
    'common/viewer/cadViewer/RENDER_NATIVE_COLORS_TOGGLE',
  RESET_VIEW: 'common/viewer/cadViewer/RESET_VIEW',
  SELECTED_OBJECTS_MANAGER_RECEIVE_SELECTIONS:
    'common/viewer/cadViewer/SELECTED_OBJECTS_MANAGER_RECEIVE_SELECTIONS',
  SELECTED_OBJECTS_MANAGER_REMOVE_ALL_SELECTIONS:
    'common/viewer/cadViewer/SELECTED_OBJECTS_MANAGER_REMOVE_ALL_SELECTIONS',
  SELECTED_OBJECTS_MANAGER_REMOVE_SELECTIONS:
    'common/viewer/cadViewer/SELECTED_OBJECTS_MANAGER_REMOVE_SELECTIONS',
  SELECTOR_TOOL_BOX_SELECT: 'common/viewer/cadViewer/SELECTOR_TOOL_BOX_SELECT',
  SELECTOR_TOOL_CLEAR_ALL_SELECTIONS:
    'common/viewer/cadViewer/SELECTOR_TOOL_CLEAR_ALL_SELECTIONS',
  SELECTOR_TOOL_DISABLE: 'common/viewer/cadViewer/SELECTOR_TOOL_DISABLE',
  SELECTOR_TOOL_ENABLE: 'common/viewer/cadViewer/SELECTOR_TOOL_ENABLE',
  SELECTOR_TOOL_RECEIVE_CUMULATIVE_INFO:
    'common/viewer/cadViewer/SELECTOR_TOOL_RECEIVE_CUMULATIVE_INFO',
  SELECTOR_TOOL_RECEIVE_SELECTION:
    'common/viewer/cadViewer/SELECTOR_TOOL_RECEIVE_SELECTION',
  SELECTOR_TOOL_REMOVE_SELECTION:
    'common/viewer/cadViewer/SELECTOR_TOOL_REMOVE_SELECTION',
  SELECTOR_TOOL_REMOVE_SELECTIONS:
    'common/viewer/cadViewer/SELECTOR_TOOL_REMOVE_SELECTIONS',
  SELECTOR_TOOL_TOGGLE: 'common/viewer/cadViewer/SELECTOR_TOOL_TOGGLE',
  SET_GEOMETRIC_INTERACTION_READY:
    'common/viewer/cadViewer/SET_GEOMETRIC_INTERACTION_READY',
  VIEWER_INTERACTIONS_FOCUS_ON_BODIES:
    'common/viewer/cadViewer/VIEWER_INTERACTIONS_FOCUS_ON_BODIES',
  VIEWER_INTERACTIONS_FOCUS_ON_PART_FILE:
    'common/viewer/cadViewer/VIEWER_INTERACTIONS_FOCUS_ON_PART_FILE',
  VIEWER_INTERACTIONS_HIDE_VIEWER_BODY:
    'common/viewer/cadViewer/VIEWER_INTERACTIONS_HIDE_VIEWER_BODY',
  VIEWER_INTERACTIONS_ISOLATE_BODIES:
    'common/viewer/cadViewer/VIEWER_INTERACTIONS_ISOLATE_BODIES',
  VIEWER_INTERACTIONS_SHOW_ALL_COMPONENTS:
    'common/viewer/cadViewer/VIEWER_INTERACTIONS_SHOW_ALL_COMPONENTS',
  VIEWER_PART_FILE_QUEUE_LOAD_SUCCESS:
    'common/viewer/cadViewer/VIEWER_PART_FILE_QUEUE_LOAD_SUCCESS',
  VIEWER_PART_FILE_QUEUE_PENDING_ADD_TO_END:
    'common/viewer/cadViewer/VIEWER_PART_FILE_QUEUE_PENDING_ADD_TO_END',
  VIEWER_PART_FILE_QUEUE_PENDING_ADD_TO_START:
    'common/viewer/cadViewer/VIEWER_PART_FILE_QUEUE_PENDING_ADD_TO_START',
  VIEWER_STATE_CAPTURE_NEXT_STATE_REQUEST:
    'common/viewer/cadViewer/VIEWER_STATE_CAPTURE_NEXT_STATE_REQUEST',
  VIEWER_STATE_RECEIVE: 'common/viewer/cadViewer/VIEWER_STATE_RECEIVE',
  VIEWER_STATE_SNAP_TO: 'common/viewer/cadViewer/VIEWER_STATE_SNAP_TO',
  VIEWER_STATE_RESET: 'common/viewer/cadViewer/VIEWER_STATE_RESET',
  WIRE_FRAME_TOOL_TOGGLE: 'common/viewer/cadViewer/WIRE_FRAME_TOOL_TOGGLE',
} as const;

export type VisibleBodiesPayload = {
  visibleBodies: string[];
};

export type ActiveBodiesPayload = {
  activeBodies: string[];
};

export type EnabledBodiesPayload = {
  enabledBodies: string[];
};

export type ExpandedBodiesPayload = {
  expandedBodies: string[];
};

export type FocusedBodiesPayload = {
  focusedBodies: string[];
};

export type SelectedBodiesPayload = {
  selectedBodies: string[];
};

export type ViewerBodyIdsPayload = {
  viewerBodyIds: string[];
};

export type PartFileUuidPayload = {
  partFileUuid: string;
};

export type ReceiveDisplayScalePayload = {
  displayScale: number;
};

export type ReceiveDisplayUnitsPayload = {
  displayUnits: PartFileUnits;
};

export type ReceiveDimensionalPrecisionPayload = {
  dimensionalPrecision: number;
};

export type ReceiveDisplayWeightUnitsPayload = {
  displayWeightUnits: WEIGHT_UNITS;
};

export type RenderNativeColorsPayload = {
  renderNativeColors: boolean;
};

export type ReceiveFocusedUuidPayload = {
  focusedUuid: string;
};

export type ReceiveForceFocusPayload = {
  forceFocus: boolean;
};

export type CadViewerForceView =
  | {
      type: 'ZOOM_TO_FIT';
      payload: { viewerId: string };
    }
  | {
      type: 'NORMAL_TO';
      payload: { intersection: AnyIntersectionTest };
    }
  | { type: 'RESET_VIEW' | 'ZOOM_TO_SELECTION' }
  | null;

export type ReceiveForceViewPayload = {
  forceView: CadViewerForceView;
};

export type ReceiveHighlightPayload = {
  highlight: Highlight[];
};

export type ReceiveHoverBodyPayload = {
  hoverBody: string | null;
};

export type ReceiveMetaDataPayload = {
  metaData: MetaData;
};

export type ReceiveNativeUnitsPayload = {
  nativeUnits: PartFileUnits;
};

export type ReceivePartFilePayload = {
  file: ViewerPartFile;
  ignoreQueue?: boolean;
  shouldFocus: boolean;
  shouldPropagateVisibility: boolean;
};

export type ShowAdditionalKeysPayload = {
  showAdditionalKeys: string[];
};

export type ShowAdditionalKeysExplicitPayload = {
  showAdditionalKeysExplicit: string[];
};

export type ReceiveShowDxfControlsPayload = {
  showDxfControls: boolean;
};

export type ReceiveTemporarilySimplifiedPayload = {
  temporarilySimplified: Set<string>;
  temporarilySimplifiedParts: Set<string>;
};

export type ReceiveTooComplexPayload = {
  tooComplex: Set<string>;
};

export type RemoveVisibleBodiesPayload = {
  viewerIds: string[];
};

export type InteractionReadyPayload = {
  isReady: boolean;
};

export type CrossSectionSetEnabledPayload = {
  enabled: boolean;
};

export type CrossSectionSetPositionPayload = {
  position: number;
};

export type CrossSectionSetPickModePayload = {
  pickMode: boolean;
};

export type CrossSectionSetNormalInput = {
  position?: number;
  x: number;
  y: number;
  z: number;
};

export type CrossSectionSetNormalPayload = {
  normal: { x: number; y: number; z: number };
  position?: number;
};

export type ExplodedViewStatePayload = {
  assemblyInstanceId: string;
  factor: number;
};

export type FeatureSelectionReceivePayload = {
  features: PreparedFeature[];
};

export type FeatureSnapToPayload = {
  feature: PreparedViewerItem | null;
};

export type GeometricPartFileBulkPreparePayload = {
  partFileUuids: string[];
};

export type MaterialStockResultKeyPayload = {
  resultKey: string;
};

export type MaterialStockFinishNestingCreationPayload = {
  result: ResultForCreation<STOCK_TYPES>;
};

export type MaterialStockReceiveNewResultPayload = {
  result: RawResultForCreation<STOCK_TYPES>;
};

export type MaterialStockVisibleResultKeysPayload = {
  resultKeys: string[];
};

export type CaliperObjectsPayload = {
  caliperObjects: LiteObject[];
};

export type MeasurementComparisonPayload = {
  comparison: Comparison | null;
};

export type MeasurementSelectionsPayload = {
  selections: LiteObject[];
  viewerState: LitState;
};

export type MeasurementDistancePayload = {
  distance: number;
};

export type ManufacturabilityFeedbackSelectionPayload = {
  manufacturabilityFeedback: PreparedFeedbackItem[];
};

export type SelectedObjectsPayload = {
  selections: LiteObject[];
};

export type SelectorToolBoxSelectPayload = {
  selections: Selection[];
};

export type SelectorCumulativeInfoPayload = {
  cumulativeInfo: {
    area: number;
    length: number;
  };
};

export type SelectorToolReceiveSelectionPayload = {
  selection: Selection;
};

export type SelectorToolRemoveSelectionPayload = {
  f?: number | null;
  obj: string;
};

export type HasPMIAnnotationsPayload = {
  hasPMIAnnotations: boolean;
};

/**
 * Shape written by getEncodedCADViewerState. Fields are optional because
 * snap-to also receives `{}` from callers that have no stored CAD state.
 */
export type EncodedCadViewerState = {
  ab?: string[];
  enb?: string[];
  exb?: string[];
  exs?: Record<string, number>;
  fb?: string[];
  fu?: string | null;
  sake?: string[];
  sb?: string[];
  vb?: string[];
};

export type ViewerStateReceivePayload = {
  viewerState: LitState;
};

export type ViewerStateSnapToPayload = {
  cadViewerState: EncodedCadViewerState;
  viewerState: LitState;
};

export type HideViewerBodyPayload = {
  viewerBodyId: string;
};

export const cadViewer = {
  appendVisibleBodies: ({ visibleBodies }: VisibleBodiesPayload) => ({
    type: types.CAD_VIEWER_APPEND_VISIBLE_BODIES,
    payload: { visibleBodies },
  }),
  receiveActiveBodies: ({ activeBodies }: ActiveBodiesPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_ACTIVE_BODIES,
    payload: { activeBodies },
  }),
  receiveDisplayScale: ({ displayScale }: ReceiveDisplayScalePayload) => ({
    type: types.CAD_VIEWER_RECEIVE_DISPLAY_SCALE,
    payload: { displayScale },
  }),
  receiveDisplayUnits: ({ displayUnits }: ReceiveDisplayUnitsPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_DISPLAY_UNITS,
    payload: { displayUnits },
  }),
  receiveDimensionalPrecision: ({
    dimensionalPrecision,
  }: ReceiveDimensionalPrecisionPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_DIMENSIONAL_PRECISION,
    payload: { dimensionalPrecision },
  }),
  receiveDisplayWeightUnits: ({
    displayWeightUnits,
  }: ReceiveDisplayWeightUnitsPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_DISPLAY_WEIGHT_UNITS,
    payload: { displayWeightUnits },
  }),
  setRenderNativeColors: ({
    renderNativeColors,
  }: RenderNativeColorsPayload) => ({
    type: types.CAD_VIEWER_SET_RENDER_NATIVE_COLORS,
    payload: { renderNativeColors },
  }),
  receiveEnabledBodies: ({ enabledBodies }: EnabledBodiesPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_ENABLED_BODIES,
    payload: { enabledBodies },
  }),
  receiveExpandedBodies: ({ expandedBodies }: ExpandedBodiesPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_EXPANDED_BODIES,
    payload: { expandedBodies },
  }),
  receiveFocusedBodies: ({ focusedBodies }: FocusedBodiesPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_FOCUSED_BODIES,
    payload: { focusedBodies },
  }),
  receiveFocusedUuid: ({ focusedUuid }: ReceiveFocusedUuidPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_FOCUSED_UUID,
    payload: { focusedUuid },
  }),
  receiveForceFocus: ({ forceFocus }: ReceiveForceFocusPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_FORCE_FOCUS,
    payload: { forceFocus },
  }),
  receiveForceView: ({ forceView }: ReceiveForceViewPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_FORCE_VIEW,
    payload: { forceView },
  }),
  receiveHighlight: ({ highlight }: ReceiveHighlightPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_HIGHLIGHT,
    payload: { highlight },
  }),
  receiveHoverBody: ({ hoverBody }: ReceiveHoverBodyPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_HOVER_BODY,
    payload: { hoverBody },
  }),
  receiveInterrogationInfo: ({
    documentHash,
    interrogationFileId,
    task,
  }: CadViewerInterrogationInfoPayload = {}) => ({
    type: types.CAD_VIEWER_RECEIVE_INTERROGATION_INFO,
    payload: { documentHash, interrogationFileId, task },
  }),
  receiveMetaData: ({ metaData }: ReceiveMetaDataPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_META_DATA,
    payload: { metaData },
  }),
  receiveNativeUnits: ({ nativeUnits }: ReceiveNativeUnitsPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_NATIVE_UNITS,
    payload: { nativeUnits },
  }),
  receivePartFile: ({
    file,
    shouldFocus,
    shouldPropagateVisibility,
    ignoreQueue,
  }: ReceivePartFilePayload) => ({
    type: types.CAD_VIEWER_RECEIVE_PART_FILE,
    payload: { file, shouldFocus, shouldPropagateVisibility, ignoreQueue },
  }),
  receiveSelectedBodies: ({ selectedBodies }: SelectedBodiesPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_SELECTED_BODIES,
    payload: { selectedBodies },
  }),
  receiveShowAdditionalKeys: ({
    showAdditionalKeys,
  }: ShowAdditionalKeysPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_SHOW_ADDITIONAL_KEYS,
    payload: { showAdditionalKeys },
  }),
  receiveShowAdditionalKeysExplicit: ({
    showAdditionalKeysExplicit,
  }: ShowAdditionalKeysExplicitPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_SHOW_ADDITIONAL_KEYS_EXPLICIT,
    payload: { showAdditionalKeysExplicit },
  }),
  receiveShowDxfControls: ({
    showDxfControls,
  }: ReceiveShowDxfControlsPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_SHOW_DXF_CONTROLS,
    payload: { showDxfControls },
  }),
  receiveVisibleBodies: ({ visibleBodies }: VisibleBodiesPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_VISIBLE_BODIES,
    payload: { visibleBodies },
  }),
  receiveTemporarilySimplified: ({
    temporarilySimplified,
    temporarilySimplifiedParts,
  }: ReceiveTemporarilySimplifiedPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_TEMPORARILY_SIMPLIFIED,
    payload: { temporarilySimplified, temporarilySimplifiedParts },
  }),
  receiveTooComplexForPartViewer: ({
    tooComplex,
  }: ReceiveTooComplexPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_TOO_COMPLEX,
    payload: { tooComplex },
  }),
  removeVisibleBodies: ({ viewerIds }: RemoveVisibleBodiesPayload) => ({
    type: types.CAD_VIEWER_REMOVE_VISIBLE_BODIES,
    payload: { viewerIds },
  }),
  reset: () => ({
    type: types.CAD_VIEWER_RESET,
  }),
  setInteractionReady: ({ isReady }: InteractionReadyPayload) => ({
    type: types.SET_GEOMETRIC_INTERACTION_READY,
    payload: { isReady },
  }),
  setGeometricEntities: ({
    showAdditionalKeysExplicit,
  }: SetGeometricEntitiesPayload = {}) => ({
    type: types.CAD_VIEWER_SET_GEOMETRIC_ENTITIES,
    payload: { showAdditionalKeysExplicit },
  }),
};

export const dimensionTool = {
  toggle: () => ({
    type: types.DIMENSION_TOOL_TOGGLE,
  }),
};

export const crossSection = {
  setEnabled: (enabled: boolean) => ({
    type: types.CROSS_SECTION_SET_ENABLED,
    payload: { enabled },
  }),
  setPosition: (position: number) => ({
    type: types.CROSS_SECTION_SET_POSITION,
    payload: { position },
  }),
  toggleInverted: () => ({
    type: types.CROSS_SECTION_TOGGLE_INVERTED,
  }),
  setPickMode: (pickMode: boolean) => ({
    type: types.CROSS_SECTION_SET_PICK_MODE,
    payload: { pickMode },
  }),
  setNormal: (arg: CrossSectionSetNormalInput) => ({
    type: types.CROSS_SECTION_SET_NORMAL,
    payload: {
      normal: { x: arg.x, y: arg.y, z: arg.z },
      ...(arg.position != null && Number.isFinite(Number(arg.position))
        ? { position: Number(arg.position) }
        : {}),
    },
  }),
};

export const optimalBoundingBoxTool = {
  toggle: () => ({
    type: types.OPTIMAL_BOUNDING_BOX_TOOL_TOGGLE,
  }),
};

export const explodedViewTool = {
  disable: () => ({
    type: types.EXPLODED_VIEW_TOOL_DISABLE,
  }),
  receiveExplodedState: ({
    assemblyInstanceId,
    factor,
  }: ExplodedViewStatePayload) => ({
    type: types.EXPLODED_VIEW_TOOL_RECEIVE_EXPLODED_STATE,
    payload: { assemblyInstanceId, factor },
  }),
  toggle: () => ({
    type: types.EXPLODED_VIEW_TOOL_TOGGLE,
  }),
};

export const featureSelection = {
  receive: ({ features }: FeatureSelectionReceivePayload) => ({
    type: types.FEATURE_SELECTION_RECEIVE,
    payload: { features },
  }),
};

export const featureSnapTo = {
  request: ({ feature }: FeatureSnapToPayload) => ({
    type: types.FEATURE_SNAP_TO_REQUEST,
    payload: { feature },
  }),
  receive: ({ feature }: FeatureSnapToPayload) => ({
    type: types.FEATURE_SNAP_TO_RECEIVE,
    payload: { feature },
  }),
};

export const geometricPartFile = {
  bulkPrepare: ({ partFileUuids }: GeometricPartFileBulkPreparePayload) => ({
    type: types.GEOMETRIC_PART_FILE_BULK_PREPARE,
    payload: { partFileUuids },
  }),
};

export const materialStock = {
  createFailed: ({ resultKey }: MaterialStockResultKeyPayload) => ({
    type: types.MATERIAL_STOCK_CREATE_FAILED,
    payload: { resultKey },
  }),
  createSuccess: ({ resultKey }: MaterialStockResultKeyPayload) => ({
    type: types.MATERIAL_STOCK_CREATE_SUCCESS,
    payload: { resultKey },
  }),
  finishNestingCreation: ({
    result,
  }: MaterialStockFinishNestingCreationPayload) => ({
    type: types.MATERIAL_STOCK_FINISH_NESTING_CREATION,
    payload: { result },
  }),
  receiveNewResult: ({ result }: MaterialStockReceiveNewResultPayload) => ({
    type: types.MATERIAL_STOCK_RECEIVE_NEW_RESULT,
    payload: { result },
  }),
  receiveVisibleResultKeys: ({
    resultKeys,
  }: MaterialStockVisibleResultKeysPayload) => ({
    type: types.MATERIAL_STOCK_RECEIVE_VISIBLE_KEYS,
    payload: { resultKeys },
  }),
};

export const measurementObjectsManager = {
  receiveSelections: ({ caliperObjects }: CaliperObjectsPayload) => ({
    type: types.MEASUREMENT_OBJECTS_MANAGER_RECEIVE_SELECTIONS,
    payload: { caliperObjects },
  }),
  removeSelections: () => ({
    type: types.MEASUREMENT_OBJECTS_MANAGER_REMOVE_SELECTIONS,
  }),
};

export const measurementTool = {
  disable: () => ({
    type: types.MEASUREMENT_TOOL_DISABLE,
  }),
  enable: () => ({
    type: types.MEASUREMENT_TOOL_ENABLE,
  }),
  receiveComparison: ({ comparison }: MeasurementComparisonPayload) => ({
    type: types.MEASUREMENT_TOOL_RECEIVE_COMPARISON,
    payload: { comparison },
  }),
  receiveSelections: ({
    selections,
    viewerState,
  }: MeasurementSelectionsPayload) => ({
    type: types.MEASUREMENT_TOOL_RECEIVE_SELECTIONS,
    payload: { selections, viewerState },
  }),
  removeSelections: () => ({
    type: types.MEASUREMENT_TOOL_REMOVE_SELECTIONS,
  }),
  receiveMeasurement: ({ distance }: MeasurementDistancePayload) => ({
    type: types.MEASUREMENT_TOOL_RECEIVE_MEASUREMENT,
    payload: { distance },
  }),
  toggle: () => ({
    type: types.MEASUREMENT_TOOL_TOGGLE,
  }),
};

export const manufacturabilityFeedbackSelection = {
  receive: ({
    manufacturabilityFeedback,
  }: ManufacturabilityFeedbackSelectionPayload) => ({
    type: types.MANUFACTURABILITY_FEEDBACK_SELECTION_RECEIVE,
    payload: { manufacturabilityFeedback },
  }),
};

export const selectedObjectsManager = {
  receiveSelections: ({ selections }: SelectedObjectsPayload) => ({
    type: types.SELECTED_OBJECTS_MANAGER_RECEIVE_SELECTIONS,
    payload: { selections },
  }),
  removeAllSelections: () => ({
    type: types.SELECTED_OBJECTS_MANAGER_REMOVE_ALL_SELECTIONS,
  }),
  removeSelections: ({ selections }: SelectedObjectsPayload) => ({
    type: types.SELECTED_OBJECTS_MANAGER_REMOVE_SELECTIONS,
    payload: { selections },
  }),
};

export const selectorTool = {
  boxSelect: ({ selections }: SelectorToolBoxSelectPayload) => ({
    type: types.SELECTOR_TOOL_BOX_SELECT,
    payload: { selections },
  }),
  clearAllSelections: () => ({
    type: types.SELECTOR_TOOL_CLEAR_ALL_SELECTIONS,
  }),
  disable: () => ({
    type: types.SELECTOR_TOOL_DISABLE,
  }),
  enable: () => ({
    type: types.SELECTOR_TOOL_ENABLE,
  }),
  receiveCumulativeInfo: ({
    cumulativeInfo,
  }: SelectorCumulativeInfoPayload) => ({
    type: types.SELECTOR_TOOL_RECEIVE_CUMULATIVE_INFO,
    payload: { cumulativeInfo },
  }),
  receiveSelection: ({ selection }: SelectorToolReceiveSelectionPayload) => ({
    type: types.SELECTOR_TOOL_RECEIVE_SELECTION,
    payload: { selection },
  }),
  removeSelection: ({ f, obj }: SelectorToolRemoveSelectionPayload) => ({
    type: types.SELECTOR_TOOL_REMOVE_SELECTION,
    payload: { f, obj },
  }),
  toggle: () => ({
    type: types.SELECTOR_TOOL_TOGGLE,
  }),
};

export const wireFrameTool = {
  toggle: () => ({
    type: types.WIRE_FRAME_TOOL_TOGGLE,
  }),
};

export const resetView = () => ({
  type: types.RESET_VIEW,
});

export const highlightViewTool = {
  toggle: () => ({
    type: types.HIGHLIGHT_VIEW_TOGGLE,
  }),
};

export const pmiViewTool = {
  show: () => ({
    type: types.PMI_VIEW_SHOW,
  }),
  hide: () => ({
    type: types.PMI_VIEW_HIDE,
  }),
  toggle: () => ({
    type: types.PMI_VIEW_TOGGLE,
  }),
  receiveHasPMIAnnotations: ({
    hasPMIAnnotations,
  }: HasPMIAnnotationsPayload) => ({
    type: types.CAD_VIEWER_RECEIVE_HAS_PMI_ANNOTATIONS,
    payload: { hasPMIAnnotations },
  }),
};

export const renderNativeColorsTool = {
  toggle: () => ({
    type: types.RENDER_NATIVE_COLORS_TOGGLE,
  }),
};

export const focusedViewToggle = () => ({
  type: types.FOCUSED_VIEW_TOGGLE,
});

/*
The viewer state is being constantly passed from the viewer to the viewer component.
When you need access to the viewer state, first you will set the flag to capture the
next step. Upon setting the flag, the CAD Viewer will trigger the receiveState action
to store the state in the redux store where it can be accessed by any listeners.
*/
export const viewerState = {
  captureNextStateRequest: () => ({
    type: types.VIEWER_STATE_CAPTURE_NEXT_STATE_REQUEST,
  }),
  receiveState: ({ viewerState }: ViewerStateReceivePayload) => ({
    // maybe this should not set the value in the store but just let someone 'take' it?
    type: types.VIEWER_STATE_RECEIVE,
    payload: { viewerState },
  }),
  snapToState: ({ viewerState, cadViewerState }: ViewerStateSnapToPayload) => ({
    type: types.VIEWER_STATE_SNAP_TO,
    payload: { viewerState, cadViewerState },
  }),
  reset: () => ({
    type: types.VIEWER_STATE_RESET,
  }),
};

export const viewerPartFileQueue = {
  loadSuccess: ({ partFileUuid }: PartFileUuidPayload) => ({
    type: types.VIEWER_PART_FILE_QUEUE_LOAD_SUCCESS,
    payload: { partFileUuid },
  }),
  pendingQueueAddToEnd: ({ partFileUuid }: PartFileUuidPayload) => ({
    type: types.VIEWER_PART_FILE_QUEUE_PENDING_ADD_TO_END,
    payload: { partFileUuid },
  }),
  pendingQueueAddToStart: ({ partFileUuid }: PartFileUuidPayload) => ({
    type: types.VIEWER_PART_FILE_QUEUE_PENDING_ADD_TO_START,
    payload: { partFileUuid },
  }),
};

export const viewerInteractions = {
  focusOnBodies: ({ viewerBodyIds }: ViewerBodyIdsPayload) => ({
    type: types.VIEWER_INTERACTIONS_FOCUS_ON_BODIES,
    payload: { viewerBodyIds },
  }),
  focusOnPartFile: ({ partFileUuid }: PartFileUuidPayload) => ({
    type: types.VIEWER_INTERACTIONS_FOCUS_ON_PART_FILE,
    payload: { partFileUuid },
  }),
  hideViewerBody: ({ viewerBodyId }: HideViewerBodyPayload) => ({
    type: types.VIEWER_INTERACTIONS_HIDE_VIEWER_BODY,
    payload: { viewerBodyId },
  }),
  isolateBodies: ({ viewerBodyIds }: ViewerBodyIdsPayload) => ({
    type: types.VIEWER_INTERACTIONS_ISOLATE_BODIES,
    payload: { viewerBodyIds },
  }),
  showAllComponents: () => ({
    type: types.VIEWER_INTERACTIONS_SHOW_ALL_COMPONENTS,
  }),
};

export type CadViewerAction = ActionsOf<{
  cadViewer: typeof cadViewer;
  crossSection: typeof crossSection;
  dimensionTool: typeof dimensionTool;
  explodedViewTool: typeof explodedViewTool;
  featureSelection: typeof featureSelection;
  featureSnapTo: typeof featureSnapTo;
  focusedViewToggle: typeof focusedViewToggle;
  geometricPartFile: typeof geometricPartFile;
  highlightViewTool: typeof highlightViewTool;
  manufacturabilityFeedbackSelection: typeof manufacturabilityFeedbackSelection;
  materialStock: typeof materialStock;
  measurementObjectsManager: typeof measurementObjectsManager;
  measurementTool: typeof measurementTool;
  optimalBoundingBoxTool: typeof optimalBoundingBoxTool;
  pmiViewTool: typeof pmiViewTool;
  renderNativeColorsTool: typeof renderNativeColorsTool;
  resetView: typeof resetView;
  selectedObjectsManager: typeof selectedObjectsManager;
  selectorTool: typeof selectorTool;
  viewerInteractions: typeof viewerInteractions;
  viewerPartFileQueue: typeof viewerPartFileQueue;
  viewerState: typeof viewerState;
  wireFrameTool: typeof wireFrameTool;
}>;
